<template>
  <div class="tender-container-wrapper">
    <div class="tender-container">
      <div
        v-show="isWappr && isPicView"
        class="callout success"
      >
        <span class="tender-font">{{ $t('hint.waitApproval', [userHelper.getUserName((general.manager || {}).username, (general.manager || {}).displayName), userHelper.getUserName((general.approver || {}).username, (general.approver || {}).displayName)]) }}</span>
      </div>
      <div
        v-show="isAppr && isPicView"
        class="callout success"
      >
        <span class="tender-font">{{ $t('hint.waitIssued', [userHelper.getUserName((general.manager || {}).username, (general.manager || {}).displayName)]) }}</span>
      </div>

      <tender-head
        ref="tenderEditHead"
        :tenderGeneral="tender.general"
      >
        <div v-if="!isCancelled">
          <button
            v-if="(isDraft || isRejected) && (isPicView || isRepresentative)"
            v-note="{text:noteText, enable:!editable}"
            :disabled="!editable || isTenderRepEditing"
            type="button"
            class="btn btn--darkblue"
            @click="goEdit()"
          >{{ $t('action.edit') }}
          </button>
          <template v-if="isPicView">
            <button
              v-if="isTender ? enableEditClosingDate && (isAppr || isWappr) : enableEditClosingDate"
              type="button"
              class="btn btn--orange"
              @click="save()"
            >{{ $t('action.save') }}</button>
            <SubmitTender
              v-show="isTenderPreview && !readonly && isFromSubmit"
              :disabled="!editable"
            />
            <button
              v-if="!(isTenderPreview || readonly) && isFromSubmit"
              type="button"
              class="btn btn--orange"
              :disabled="!editable"
              @click="issue()"
            >{{ $t('action.issue') }}</button>
          </template>
          <template v-if="isTmView && isWappr">
            <reject-tender :tenderInfo.sync="general" />
            <button
              type="button"
              class="btn btn--orange"
              @click="approve()"
            >{{ $t('action.approve') }}</button>
          </template>
        </div>
      </tender-head>

      <RecentAddendum
        v-if="tender.general.version"
        :isBid="false"
      >
        <template
          slot="vendor-bid-status"
        >
          <VendorBidStatusCollapse
            v-if="showVendorStatusList"
          />
        </template>
      </RecentAddendum>

      <div v-if="!isTenderPreview">
        <closing-date
          ref="closingDate"
          :partialClosingDate="partialClosingDate"
          @update:partialClosingDate="val => partialClosingDate = val"
        />
      </div>

      <tender-tabs-container :isTender="isTender">
        <div v-if="!isCancelled">
          <button
            v-if="(isDraft || isRejected) && (isPicView || isRepresentative)"
            v-note="{text:noteText, enable:!editable}"
            :disabled="!editable"
            type="button"
            class="btn btn--darkblue mini-btn"
            @click="goEdit()"
          >{{ $t('action.edit') }}
          </button>
          <template v-if="isPicView">
            <button
              v-if="isTender ? enableEditClosingDate && (isAppr || isWappr) : enableEditClosingDate"
              type="button"
              class="btn btn--orange mini-btn"
              @click="save()"
            >{{ $t('action.save') }}</button>
            <SubmitTender
              v-show="isTenderPreview && !readonly && isFromSubmit"
              id="sticky"
              :className="'btn btn--orange mini-btn'"
              :disabled="!editable"
            />
            <button
              v-if="!(isTenderPreview || readonly) && isFromSubmit"
              type="button"
              class="btn btn--orange mini-btn"
              @click="issue()"
            >{{ $t('action.issue') }}</button>
          </template>
          <template v-if="isTmView && isWappr">
            <reject-tender
              id="sticky"
              :tenderInfo.sync="general"
              :className="'btn btn--darkblue mini-btn'"
            />
            <button
              type="button"
              class="btn btn--orange mini-btn"
              @click="approve()"
            >{{ $t('action.approve') }}</button>
          </template>
        </div>

        <general
          v-if="tenderId === currentTender.general.id"
          slot="general"
        />
        <scope-of-work slot="scopeOfWork" />
        <items-services slot="itemServices" />
        <attachments
          slot="fileUpload"
          :files="attachment.files"
          :description="attachment.description"
          :zipName="zipNames.attachments"
        />
        <terms-conditions
          slot="termsConditions"
          :tenderId="tenderId"
          :isTender="isTender"
          :zipName="zipNames.terms"
        />
      </tender-tabs-container>

      <TenderRepresentative
        v-show="showTenderRep"
        :isEditing.sync="isTenderRepEditing"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { ref } from 'vue-demi'
import Attachments from '@/modules/tenders/view/Attachments'
import ClosingDate from '@/modules/tenders/ClosingDate'
import General from '@/modules/tenders/view/General'
import ItemsServices from '@/modules/tenders/view/ItemsServices'
import RecentAddendum from '@/modules/tenders/addendum/RecentAddendum'
import RejectTender from '@/modules/tenders/RejectTender'
import ScopeOfWork from '@/modules/tenders/view/ScopeOfWork'
import SubmitTender from '@/modules/tenders/SubmitTender'
import TenderHead from '@/modules/tenders/TenderHead'
import tenderRepShowMixin from '@/modules/tenders/tenderRepShowMixin'
import TenderRepresentative from '@/modules/tenders/view/TenderRepresentative'
import TenderTabsContainer from '@/modules/tenders/TenderTabsContainer'
import TermsConditions from '@/modules/tenders/TermsConditions'
import VendorBidStatusCollapse from '@/modules/tenders/view/VendorBidStatusCollapse'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import { getAllowServiceAddOthersUpdated } from '@/utils/tenderUtils'
import { getAttachments } from '@/api/docs/request'
import { resetBidsToDraft } from '@/api/bids/request'
import { sendEmail } from '@/api/postman/request'
import { tenderStatus, addendumChangeType } from '@/utils/locale'
import checkEditableMixin from '@/modules/tenders/checkEditableMixin'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import router from '@/router'
import userHelper from '@/utils/userHelper'

export default {
  name: 'TenderView',
  components: {
    General,
    ScopeOfWork,
    ItemsServices,
    TenderHead,
    TermsConditions,
    SubmitTender,
    RejectTender,
    Attachments,
    ClosingDate,
    TenderTabsContainer,
    RecentAddendum,
    TenderRepresentative,
    VendorBidStatusCollapse,
  },
  mixins: [checkEditableMixin, tenderRepShowMixin],
  setup () {
    const isTenderRepEditing = ref(false)
    return {
      isTenderRepEditing,
    }
  },
  data () {
    return {
      active: 'mobile-tabls-general',
      tenderId: null,
      attachmentFiles: [],
      invalidContactInfo: false,
      partialClosingDate: false,
      showConfirm: false,
      homePageUrl: '/',
      showMessage: false,
      isInfo: true,
      isError: false,
      isFromSubmit: false,
      message: '',
      userHelper,
    }
  },
  computed: {
    ...mapGetters('tenders', [
      'currentTender', 'isAddendum', 'isRepresentative', 'zipNames',
    ]),
    ...mapGetters('user', ['currentUser', 'isContractor']),
    // TODO @Gino remove after sub tabs all migrate to vuex
    attachment () {
      return {
        description: this.currentTender && this.currentTender.general ? this.currentTender.general.attachmentDescription : '',
        files: this.attachmentFiles || [],
      }
    },
    tender () { return this.currentTender },
    general () { return this.currentTender.general || {} },
    scopeOfWorks () {
      return this.general.scopeOfWorks || {
        generalDesc: '',
        generalSpec: '',
        particularDesc: '',
      }
    },

    isTender () { return this.currentTender.general.tenderType === 'TENDER' },
    isTenderPreview () {
      return (this.isTender &&
        (this.general.status === tenderStatus.DRAFT ||
          this.general.status === tenderStatus.WAPPR || this.general.status === tenderStatus.REJECTED))
    },
    isDraft () {
      return this.general.status === tenderStatus.DRAFT
    },
    isAppr () {
      return this.general.status === tenderStatus.APPR
    },
    isCancelled () {
      return this.general.status === tenderStatus.CANCELLED
    },
    isWappr () {
      return (this.general.status === tenderStatus.WAPPR ||
        this.general.status === tenderStatus.A_WAPPR)
    },
    isRejected () {
      return this.general.status === tenderStatus.REJECTED
    },
    isPicView () {
      return this.currentUser.id === this.currentTender.general.managedBy
    },
    isTmView () {
      return this.currentUser.id === this.general.approvedBy && aclHelper.getAccess(aclPermsType.tenders.approve)
    },
    readonly () {
      const status = this.general.status || ''
      return status.endsWith(tenderStatus.ISSUED) ||
        status.endsWith(tenderStatus.WAPPR) ||
        status.endsWith(tenderStatus.READY)
    },
    closingDateUpdateRequired () {
      return this.isAddendum && this.general.closingDateExtended
    },
    enableEditClosingDate () {
      return !this.readonly && this.isPicView &&
        (!this.isAddendum || this.closingDateUpdateRequired)
    },
    showVendorStatusList () {
      return !(this.isTender
        ? this.enableEditClosingDate && (this.isAppr || this.isWappr)
        : this.enableEditClosingDate)
    },
  },
  async mounted () {
    this.tenderId = router.currentRoute.params.id
    await this.loadTender(this.tenderId)
    if (!this.validateRoute()) return
    this.isFromSubmit = router.currentRoute.params.isFromSubmit || (!this.isDraft && !this.isRejected)
    await this.loadRecentRevision({ loadPendingAddendum: true })
    // TODO @Gino remove after attachement tab implemented
    await this.initAttachments()
    // check edit button status for rep review
    if (!this.isCancelled && (this.isPicView || this.isRepresentative) && (this.isDraft || this.isRejected)) {
      await this.checkEditable(this.tenderId)
    }
  },

  methods: {
    // TODO @Gino remove after sub tabs all migrate to vuex
    async initAttachments () {
      const files = await getAttachments(this.tenderId)
      this.attachmentFiles = files
    },

    async resetBidsAfterAddendum () {
      if (this.isAddendum) {
        await resetBidsToDraft(this.tenderId, this.currentTender)
      }
    },

    async doSendEmail () {
      try {
        const results = await sendEmail(this.isAddendum ? EmailTypes.tenders.addendum : EmailTypes.tenders.invitation, null, false)
        const failedEmails = results.filter(result => !result.isSuccess).map(result => result.to)
        if (failedEmails.length > 0) {
          const message = `${this.$i18n.t('error.sendmail.toVendors.500')}\n\t${failedEmails.join('\n\t')}`
          notification.fireNotification(false, message)
          return false
        }
      } catch (err) {
        notification.fireNotification(false, err.message)
        throw err
      }
      return true
    },

    async save () {
      if (this.partialClosingDate) {
        this.updateTenderPathField({
          path: ['general', 'closingDate'],
          newValue: null,
        })
      }
      if (this.isAddendum) {
        this.updateItemServicesLabel()
      }
      await this.saveCurrentTender({ showNotification: true, updatedBidStatus: true })
      await this.refreshTender()
    },

    lockOnSiteRequired () {
      if (this.general.onsiteVisitRequired) {
        const onsiteContact = lodash.cloneDeep(this.general.onsiteContact)
        delete onsiteContact.changedFlag
        this.updateTenderPathField({
          path: ['general', 'onsiteContact'],
          newValue: onsiteContact,
        })
      }
    },

    async issue () {
      await this.checkEditable(this.tenderId)
      if (!this.editable) return
      const bakClosingDateExtended = this.general.closingDateExtended
      try {
        this.highlightflags = []
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        if (!this.$refs.closingDate.validate()) return

        if (this.isAddendum) {
          this.updateItemServicesLabel()
          this.updateAllowServiceAddOthersUpdated()
        }
        const sendEmailSuccess = await this.doSendEmail()
        if (!sendEmailSuccess) {
          return
        }
        this.updateTenderPathField({
          path: ['general', 'closingDateExtended'],
          newValue: 0,
        })
        this.lockOnSiteRequired()
        await this.saveCurrentTender({ showNotification: false, updatedBidStatus: true })

        const action = this.isAddendum ? ActionTypes.tenders.addendum.issue : ActionTypes.tenders.issue
        await this.updateTenderStatus({
          id: this.tenderId,
          status: tenderStatus.ISSUED,
          action })
        await this.refreshTender()
        await this.resetBidsAfterAddendum()
        navigation.toHome()
      } catch (err) {
        console.error(err)
        notification.handleResponseError(err)
        this.updateTenderPathField({
          path: ['general', 'closingDateExtended'],
          newValue: bakClosingDateExtended,
        })
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },

    updateItemServicesLabel () {
      const services = this.currentTender.service || []
      const servicesPath = ['service']
      for (let i = 0; i < services.length; i += 1) {
        const service = services[i]
        const sectionPath = [...servicesPath, i]
        if (service.forReference && !service.checked && service.addendumLabel === addendumChangeType.pending.removed) {
          service.lineItems.forEach((line, index) => {
            const linePath = [...sectionPath, 'lineItems', index]
            this.updateChangedLabelForAddendum(linePath, addendumChangeType.pending.removed)
          })
        }
        this.updateChangedLabelForAddendum(sectionPath, service.addendumLabel)
        const lineItems = service.lineItems || []
        for (let j = 0; j < lineItems.length; j += 1) {
          const lineItem = lineItems[j]
          const linePath = [...sectionPath, 'lineItems', j]
          this.updateChangedLabelForAddendum(linePath, lineItem.addendumLabel)
          const options = lineItem.options || []
          for (let k = 0; k < options.length; k += 1) {
            const optionPath = [...linePath, 'options', k]
            this.updateChangedLabelForAddendum(optionPath, options[k].addendumLabel)
          }
        }
      }
    },
    updateChangedLabelForAddendum (itemPath, label) {
      if (!label) return
      const newValue = label.substring(0, 1)
      this.updateTenderPathField({
        path: [...itemPath, 'addendumLabel'],
        newValue,
      })
    },
    updateAllowServiceAddOthersUpdated () {
      const updated = getAllowServiceAddOthersUpdated(this.currentTender)
      if (updated) {
        this.updateTenderField({
          sectionName: 'general',
          fieldName: 'allowServiceAddOthersUpdated',
          newValue: true,
        })
      }
    },

    async approve () {
      const action = this.isAddendum ? ActionTypes.tenders.addendum.approve : ActionTypes.tenders.approve
      await this.updateTenderStatus({
        id: this.tenderId,
        status: tenderStatus.APPR,
        action })
      await this.refreshTender()
      await sendEmail(EmailTypes.tenders.approvalResult)
    },

    async goEdit () {
      await this.checkEditable(this.tenderId)
      if (!this.editable) return
      let editview = this.isContractor ? 'tenderDetailsExt' : 'tenderDetails'
      this.$router.push({
        name: editview,
        params: {
          isEditing: true,
          id: this.tenderId,
        },
      })
    },
    ...mapActions('tenders', [
      'loadTender',
      'validateRoute',
      'refreshTender',
      'saveCurrentTender',
      'loadRecentRevision',
      'updateTenderStatus',
    ]),
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
      updateTenderField: mutationTypes.TENDER_UPDATE_FIELD,
    }),
  },

}
</script>

<style lang="scss" scoped>
@import "~styles/stickyBtn";

.closing-date-wrapper {
  background: white;
}
.tender-preview-hint {
  margin-bottom: 1vh;
  float: left;
}

.callout {
  margin: 0;
  padding: 0.8rem 1.3rem;
}

.callout.alert {
  background-color: #ffebee;
  border: solid 1px #f44336;

  .tender-font {
    color: #f44336;
  }
}

.callout.success {
  background-color: #e0f2f1;
  border: solid 1px #009588;

  .tender-font {
    color: #009588;
  }
}
</style>
